import { useContext, useMemo } from 'react'
import { useSocketConnectionContext } from 'src/Elements/Framework/SystemDataProvider'
import { useDynamicPowerPricesEnabledContext } from 'src/Libs/dynamic-power-prices/context/dynamic-power-prices-enabled-provider'
import { useInstallationDevices, useInstallationSubDevices } from 'src/Libs/flow/installation/hooks/installation-fetch-hooks'
import { parseChargingStrategy } from 'src/Libs/flow/parsers'
import { isFlowTheme } from 'src/Libs/utils'
import { useWallboxes, WallboxConfigurationModel } from 'src/Libs/wallboxes'
import { EmobilityWallboxConfigurationDto } from 'src/Libs/wallboxes/dto/wallbox-configuration.dto'
import { useEmobilityWallboxConfigurationQueries, useWallboxHardwareByStorage } from 'src/Libs/wallboxes/hooks'
import { WallboxChargingModel, WallboxHardwareModel } from 'src/Libs/wallboxes/models'
import { ChargeStrategyTypeEnum } from 'src/Libs/wallboxes/types'
import { ChargingStatus, EmobiityChargingStatusCode } from 'src/Routes/EMobility/EMobilityModels'

export type MappedWallboxForWidgets = WallboxHardwareModel & {
  chargingData?: WallboxChargingModel
  currentPowerPrice?: number
  wallboxLimit?: number
  isAvailable?: boolean
  isBoostModeActive?: boolean
  hasSmartComponent?: boolean
}

export type WallboxWidgetsData = {
  mappedWallboxesForWidget: MappedWallboxForWidgets[]
  installationId?: string
  storageId?: string
  isWallboxWidgetDataLoading: boolean
}

//#region Wallbox Widgets Data Hook
/*
 * A hook that returns the data needed for the wallbox widgets.
 */
const useDataForWallboxWidgets = ({
  currentPowerPrice,
  wallboxLimit,
  installationId,
  storageId,
}: {
  currentPowerPrice?: number
  wallboxLimit?: number
  installationId?: string
  storageId?: string
}): WallboxWidgetsData => {
  const { isDynamicPowerPriceEnabledForWallboxes } = useDynamicPowerPricesEnabledContext()
  const { wallboxChargingSocketState } = useContext(useSocketConnectionContext())
  const { data: hardwareWallboxes, isLoading: areHardwareWallboxesLoading } = useWallboxHardwareByStorage()
  const { wallboxes: configurationWallboxes, isLoading: areConfigurationWallboxesLoading } = useWallboxes()

  const installationIdNumber = installationId ? Number.parseInt(installationId) : undefined
  const { data: devices } = useInstallationDevices(installationIdNumber, isFlowTheme)
  const emcId = devices?.emc?.id?.toString() ?? null
  const { data: subDevices } = useInstallationSubDevices(installationIdNumber, emcId, isFlowTheme)

  const emobilityWallboxConfigurationQueries = useEmobilityWallboxConfigurationQueries()
  const mappedWallboxesForWidget: MappedWallboxForWidgets[] = useMemo(() => {
    const orderedLiveWallboxData = wallboxChargingSocketState?.data?.sort(
      (a, b) => new Date(b.startAt || new Date()).getTime() - new Date(a.startAt || new Date()).getTime()
    )

    return (
      hardwareWallboxes
        ?.map(hardwareWallbox => {
          const matchingConfigurationWallbox = configurationWallboxes.find(
            configurationWallbox =>
              configurationWallbox.wallboxIndex === hardwareWallbox.number &&
              configurationWallbox.storageSerialNo === hardwareWallbox.storageSerialNumber.toString()
          )
          const matchingFlowConfigurationWallbox = emobilityWallboxConfigurationQueries?.find(cw => cw.data?.index === hardwareWallbox.number)?.data

          const matchingChargingData = getMatchingChargingData(
            hardwareWallbox,
            orderedLiveWallboxData,
            matchingConfigurationWallbox,
            matchingFlowConfigurationWallbox
          )

          const isChargeStrategyTypeDisabled = matchingConfigurationWallbox?.chargeStrategy?.chargeStrategyType === ChargeStrategyTypeEnum.disabled
          const hasActiveChargeStrategy = matchingConfigurationWallbox?.chargeStrategyConfiguration?.some(
            ({ chargeStrategyDirectChargeAmount, chargeStrategyDelayedChargeAmount }) =>
              chargeStrategyDirectChargeAmount !== 0 || chargeStrategyDelayedChargeAmount !== 0
          )

          const matchingFlowEvse = subDevices?.controlled?.find(evse => {
            const matchingSubDeviceLink = subDevices?.controlled?.find(
              subDevice =>
                // ? Unfortunately for who knows what reason, wallboxId isn't always present ¯\_(ツ)_/¯
                subDevice.evseSubTypeParameters?.wallboxId === hardwareWallbox.wallboxId ||
                (subDevice?.mediaParameters &&
                  'macAddress' in subDevice.mediaParameters &&
                  subDevice.mediaParameters?.macAddress === hardwareWallbox.mac) ||
                subDevice.deviceName === hardwareWallbox.deviceName
            )
            return evse.pairingState === 'paired' && Boolean(matchingSubDeviceLink)
          })

          const chargingMode = parseChargingStrategy(matchingFlowEvse?.parameters?.sunMode ?? null)
          const hasFlowSmartComponents = chargingMode === 'pvImmediate' || chargingMode === 'pvDelayed' || isDynamicPowerPriceEnabledForWallboxes
          const isFlowSunModeActive = matchingFlowEvse?.parameters?.sunMode?.activated
          const isSunModeActiveThemed = isFlowTheme ? isFlowSunModeActive : matchingConfigurationWallbox?.sunModeActive

          return {
            ...hardwareWallbox,
            sunMode: isSunModeActiveThemed ? 1 : 0,
            chargingData: matchingChargingData,
            currentPowerPrice,
            wallboxLimit,
            isAvailable: matchingConfigurationWallbox?.isAvailable,
            isBoostModeActive: matchingConfigurationWallbox?.isBoostModeActive,
            hasSmartComponent: isFlowTheme
              ? hasFlowSmartComponents
              : isDynamicPowerPriceEnabledForWallboxes || (hasActiveChargeStrategy && !isChargeStrategyTypeDisabled),
          }
        })
        .sort((a, b) => getStatusOrder(a.chargingData?.chargingStatus) - getStatusOrder(b.chargingData?.chargingStatus))
        .sort((a, b) => Number(b.isAvailable) - Number(a.isAvailable)) || []
    )
  }, [
    isDynamicPowerPriceEnabledForWallboxes,
    hardwareWallboxes,
    wallboxChargingSocketState?.data,
    currentPowerPrice,
    wallboxLimit,
    configurationWallboxes,
    subDevices,
    emobilityWallboxConfigurationQueries,
  ])

  const isSocketDataLoading = !wallboxChargingSocketState?.connected && !wallboxChargingSocketState?.error

  const isWallboxWidgetDataLoading = areConfigurationWallboxesLoading || areHardwareWallboxesLoading || isSocketDataLoading

  return {
    mappedWallboxesForWidget,
    installationId,
    storageId,
    isWallboxWidgetDataLoading,
  }
}

export default useDataForWallboxWidgets
//#endregion

//#region Helper Functions
const getStatusOrder = (status: string | undefined): number => {
  if (status === 'CHARGING') return 0
  if (status === 'DISCHARGING') return 1
  if (status === undefined) return 3
  return 2 /* For all other statuses */
}

const emobilityChargingStatusCodeMap: Record<EmobiityChargingStatusCode, ChargingStatus> = {
  '-1': 'NOT_CHARGED',
  '0': 'CHARGING',
  '1': 'WAIT',
  '2': 'WAIT_PV',
  '3': 'WAIT_AUTHENTICATION',
  '4': 'FINISHED',
  '5': 'FINISHED_NO_ACTION',
  '6': 'ERROR',
  '7': 'UNAUTHORIZED',
  '8': 'EMERGENCY_EXIT',
  '9': 'UNKNOWN',
  '10': 'DISCHARGING',
  '11': 'SETUP',
  '12': 'STANDBY',
  '13': 'FULL',
}

const extendWithEmobilityConfigurationData = (emobilityConfiguration: EmobilityWallboxConfigurationDto): WallboxChargingModel => ({
  chargingStatus: emobilityChargingStatusCodeMap[emobilityConfiguration?.sessionStatus],
  energyAll: (emobilityConfiguration?.pmEnergyL1 || 0) + (emobilityConfiguration?.pmEnergyL2 || 0) + (emobilityConfiguration?.pmEnergyL3 || 0),
})

/**
 * Helpers to find the matching charging data for a given wallbox hardware. According to the theme.
 * It first checks the live wallbox data, then the emobility configuration, and finally the configuration wallbox.
 * @param hardwareWallbox Wallbox hardware model to find the matching charging data for
 * @param orderedLiveWallboxData Wallbox charging data from the socket connection, ordered by startAt date
 * @param matchingConfigurationWallbox Wallbox configuration model to find the matching charging data for
 * @param matchingFlowConfigurationWallbox Flow wallbox configuration DTO to find the matching charging data for
 * @returns The matching wallbox charging data, or undefined if none is found
 */
const getMatchingChargingData = (
  hardwareWallbox: WallboxHardwareModel,
  orderedLiveWallboxData?: WallboxChargingModel[],
  matchingConfigurationWallbox?: WallboxConfigurationModel,
  matchingFlowConfigurationWallbox?: EmobilityWallboxConfigurationDto
): WallboxChargingModel | undefined => {
  let matchingChargingData = orderedLiveWallboxData?.find(liveWallbox => liveWallbox.wallboxId === hardwareWallbox?.wallboxId)

  if (matchingChargingData) return matchingChargingData

  if (isFlowTheme && matchingFlowConfigurationWallbox) {
    matchingChargingData = extendWithEmobilityConfigurationData(matchingFlowConfigurationWallbox)
  }
  if (!isFlowTheme) {
    matchingChargingData = orderedLiveWallboxData?.find(liveWallbox => liveWallbox.wallboxId === matchingConfigurationWallbox?.serialNo)
  }

  return matchingChargingData
}
//#endregion
